<template>
  <div class="operate-config-page">
    <a-card :bordered="false" title="外部配置">
      <div class="toolbar">
        <a-input
          v-model:value="query.keyword"
          placeholder="广告主ID/产品编码/备注"
          allow-clear
          style="width: 220px"
          @pressEnter="fetchList"
        />
        <a-input-number
          v-model:value="query.appId"
          placeholder="应用ID"
          :min="1"
          style="width: 120px"
        />
        <a-input-number
          v-model:value="query.goodId"
          placeholder="商品ID"
          :min="1"
          style="width: 120px"
        />
        <a-select
          v-model:value="query.available"
          placeholder="状态"
          allow-clear
          style="width: 110px"
        >
          <a-select-option :value="true">可用</a-select-option>
          <a-select-option :value="false">不可用</a-select-option>
        </a-select>
        <a-button type="primary" @click="fetchList">查询</a-button>
        <a-button @click="resetQuery">重置</a-button>
      </div>

      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        size="small"
        bordered
        :scroll="{ x: 'max-content' }"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'available'">
            <a-tag :color="record.available ? 'green' : 'red'">
              {{ record.available ? '可用' : '不可用' }}
            </a-tag>
          </template>
          <template v-if="column.dataIndex === 'notifyUrl'">
            <a-tooltip :title="record.notifyUrl">
              <span class="ellipsis-cell">{{ record.notifyUrl || '-' }}</span>
            </a-tooltip>
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      title="编辑外部配置"
      width="760px"
      :confirm-loading="saving"
      @ok="handleSave"
      @cancel="modalVisible = false"
    >
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="ID">
          <a-input-number v-model:value="form.id" disabled style="width: 100%" />
        </a-form-item>
        <a-form-item label="商品ID">
          <a-input-number v-model:value="form.goodId" :min="1" style="width: 100%" />
        </a-form-item>
        <a-form-item label="应用ID">
          <a-input-number v-model:value="form.appId" :min="1" style="width: 100%" />
        </a-form-item>
        <a-form-item label="广告主ID">
          <a-input v-model:value="form.advertisingId" allow-clear />
        </a-form-item>
        <a-form-item label="产品编码">
          <a-input v-model:value="form.productCode" allow-clear />
        </a-form-item>
        <a-form-item label="支付渠道">
          <a-select v-model:value="form.payType">
            <a-select-option :value="0">快钱</a-select-option>
            <a-select-option :value="1">合利宝</a-select-option>
            <a-select-option :value="2">京东</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="权重">
          <a-input-number v-model:value="form.weight" :min="0" style="width: 100%" />
        </a-form-item>
        <a-form-item label="状态">
          <a-switch v-model:checked="form.available" checked-children="可用" un-checked-children="不可用" />
        </a-form-item>
        <a-form-item label="回调地址">
          <a-textarea v-model:value="form.notifyUrl" :rows="3" allow-clear />
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-model:value="form.remark" :rows="3" allow-clear />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from "vue";
import { message } from "ant-design-vue";
import { get, postJson } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";

const query = reactive({
  keyword: "",
  appId: undefined,
  goodId: undefined,
  available: undefined,
});

const loading = ref(false);
const saving = ref(false);
const modalVisible = ref(false);
const list = ref([]);
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`,
});

const columns = [
  { title: "ID", dataIndex: "id", width: 80, fixed: "left" },
  { title: "应用", dataIndex: "appName", width: 180 },
  { title: "应用ID", dataIndex: "appId", width: 90 },
  { title: "商品", dataIndex: "goodName", width: 180 },
  { title: "商品ID", dataIndex: "goodId", width: 90 },
  { title: "广告主ID", dataIndex: "advertisingId", width: 130 },
  { title: "产品编码", dataIndex: "productCode", width: 120 },
  { title: "支付渠道", dataIndex: "payTypeName", width: 100 },
  { title: "权重", dataIndex: "weight", width: 80 },
  { title: "状态", dataIndex: "available", width: 90 },
  { title: "回调地址", dataIndex: "notifyUrl", width: 260 },
  { title: "备注", dataIndex: "remark", width: 160 },
  { title: "更新时间", dataIndex: "updatedAt", width: 170 },
  { title: "操作", dataIndex: "action", width: 90, fixed: "right" },
];

const form = reactive({
  id: undefined,
  goodId: undefined,
  appId: undefined,
  advertisingId: "",
  productCode: "",
  payType: 0,
  notifyUrl: "",
  weight: 0,
  available: true,
  remark: "",
});

const resetQuery = () => {
  query.keyword = "";
  query.appId = undefined;
  query.goodId = undefined;
  query.available = undefined;
  pagination.current = 1;
  fetchList();
};

const fetchList = async () => {
  loading.value = true;
  try {
    const json = await get(analyticsUrl("/operate/external-agreement-conf/list"), {
      pageIdx: pagination.current,
      pageSize: pagination.pageSize,
      keyword: query.keyword,
      appId: query.appId,
      goodId: query.goodId,
      available: query.available,
    });
    if (json.code === 200) {
      list.value = json.data?.data || [];
      pagination.total = json.data?.dataSum || 0;
      pagination.current = json.data?.pageIdx || pagination.current;
      pagination.pageSize = json.data?.pageSize || pagination.pageSize;
    } else {
      message.error(json.content || "查询失败");
    }
  } finally {
    loading.value = false;
  }
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchList();
};

const openEdit = (record) => {
  form.id = record.id;
  form.goodId = record.goodId;
  form.appId = record.appId;
  form.advertisingId = record.advertisingId;
  form.productCode = record.productCode;
  form.payType = record.payType;
  form.notifyUrl = record.notifyUrl;
  form.weight = record.weight;
  form.available = record.available;
  form.remark = record.remark;
  modalVisible.value = true;
};

const handleSave = async () => {
  if (!form.id) {
    message.warning("记录ID不能为空");
    return;
  }
  saving.value = true;
  try {
    const json = await postJson(analyticsUrl("/operate/external-agreement-conf/update"), { ...form });
    if (json.code === 200) {
      message.success("保存成功");
      modalVisible.value = false;
      fetchList();
    } else {
      message.error(json.content || "保存失败");
    }
  } finally {
    saving.value = false;
  }
};

onMounted(fetchList);
</script>

<style scoped>
.operate-config-page {
  padding: 24px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.ellipsis-cell {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
}
</style>
